import { useEffect, useRef, useState } from 'react'
import type { Editor } from '@tiptap/react'
import { Bold, Italic } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface EditorBubbleMenuProps {
  editor: Editor
}

export function EditorBubbleMenu({ editor }: EditorBubbleMenuProps) {
  const menuRef = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ top: 0, left: 0 })

  useEffect(() => {
    const updateMenu = () => {
      const { from, to, empty } = editor.state.selection
      if (empty || from === to) {
        setVisible(false)
        return
      }

      const view = editor.view
      const start = view.coordsAtPos(from)
      const end = view.coordsAtPos(to)
      const editorRect = view.dom.closest('.bg-white')?.getBoundingClientRect()
      if (!editorRect) return

      setPosition({
        top: start.top - editorRect.top - 48,
        left: (start.left + end.left) / 2 - editorRect.left,
      })
      setVisible(true)
    }

    editor.on('selectionUpdate', updateMenu)
    editor.on('blur', () => setVisible(false))

    return () => {
      editor.off('selectionUpdate', updateMenu)
    }
  }, [editor])

  if (!visible) return null

  return (
    <div
      ref={menuRef}
      className="absolute z-50 flex items-center gap-0.5 rounded-lg border border-border bg-background p-1 shadow-md"
      style={{ top: position.top, left: position.left, transform: 'translateX(-50%)' }}
    >
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 ${editor.isActive('bold') ? 'bg-accent' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault()
          editor.chain().focus().toggleBold().run()
        }}
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className={`h-8 w-8 ${editor.isActive('italic') ? 'bg-accent' : ''}`}
        onMouseDown={(e) => {
          e.preventDefault()
          editor.chain().focus().toggleItalic().run()
        }}
      >
        <Italic className="h-4 w-4" />
      </Button>
    </div>
  )
}
